import { useAppSelector } from 'hooks';
import classNames from 'classnames';
import { useState } from 'react';

import { PlusCircleIcon, CloseIcon, InfoCircleIcon } from 'components/icons';
import PermissionSection from 'components/permission/PermissionSection';
import { BusinessProductXrefSchema } from 'services/businessProducts';
import { ProductTypeCodes } from 'entities/progress';
import { getEnumKeyByValue } from 'utils/helpers';
import { Permissions } from 'entities/dashboard';
import Popover from 'components/Popover';
import Loading from 'components/Loading';

import { getIsCapitalProductActive } from '../selectors';
import OatFiCreditLimit from './OatFiCreditLimit';
import { getProductPopover } from '../utils';

type ProgressProductProps = {
  item: BusinessProductXrefSchema;
  onClick: (key: BusinessProductXrefSchema) => Promise<void>;
  inline: boolean;
  permission: boolean;
  disabled: boolean;
  hasComplianceOrImplementationsPermissions: boolean;
};

const ProgressProduct: React.FC<ProgressProductProps> = ({
  item,
  onClick,
  inline,
  permission,
  disabled,
  hasComplianceOrImplementationsPermissions,
}) => {
  const isCapitalProductActive = useAppSelector(getIsCapitalProductActive);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const popoverContent = getProductPopover(
    getEnumKeyByValue(ProductTypeCodes, item.productsTypeCode),
  );

  const showRemoveButton = () => {
    if (!item.isActive || item.productsTypeCode === ProductTypeCodes.capital) {
      return false;
    }

    if (
      item.productsTypeCode === ProductTypeCodes.payments &&
      (!isCapitalProductActive || !hasComplianceOrImplementationsPermissions)
    ) {
      return false;
    }

    return true;
  };

  const handleClick = async (product: BusinessProductXrefSchema) => {
    if (!permission || isLoading) {
      return;
    }

    setIsLoading(true);
    await onClick(product);
    setIsLoading(false);
  };

  return (
    <div className={classNames(inline ? 'mr-2' : 'w-full', 'mt-3 rounded-lg')}>
      <PermissionSection
        permission={item.isActive ? undefined : Permissions.products}
        showPopover={!item.isActive}
        fullWidth
        edit
      >
        <div
          aria-hidden="true"
          className={classNames(
            'text-gray-400 px-[10px] py-2 w-full rounded-md relative border border-gray-200',
            {
              'hover:text-blue-600 hover:bg-gray-50 cursor-pointer':
                !item.isActive && !disabled,
              'cursor-default': item.isActive,
              'opacity-40': !item.isActive && (disabled || !permission),
              'cursor-not-allowed': !permission,
              'bg-gray-100': disabled,
              'bg-white': !disabled,
            },
          )}
          onClick={() => (item.isActive || disabled ? null : handleClick(item))}
        >
          <Loading loading={isLoading} transparent />

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="py-px flex-shrink-0">
                {item.isActive ? (
                  <img
                    alt={item.productsDescription || ''}
                    src={`/assets/images/product-${getEnumKeyByValue(
                      ProductTypeCodes,
                      item.productsTypeCode,
                    )}.svg`}
                    width="32"
                    height="32"
                  />
                ) : (
                  <PlusCircleIcon />
                )}
              </div>

              <span
                className={classNames(
                  'text-sm leading-5 tracking-tight font-medium mx-2',
                  item.isActive ? 'text-gray-800' : 'text-blue-600',
                )}
              >
                {item.productsDescription}
              </span>
            </div>

            <div className="flex items-center">
              {popoverContent && (
                <Popover
                  content={popoverContent}
                  button={
                    <button className="flex self-center cursor-pointer text-gray-400 flex-shrink-0 p-0.5 rounded-md hover:bg-gray-100 hover:text-gray-500">
                      <InfoCircleIcon className="h-[18px] w-[18px] text-gray-500" />
                    </button>
                  }
                  stopPropagation
                />
              )}

              {showRemoveButton() && (
                <PermissionSection
                  permission={Permissions.products}
                  showPopover
                  edit
                >
                  <button
                    className="ml-1 cursor-pointer text-gray-400 flex-shrink-0 p-0.5 rounded-md hover:bg-gray-100 hover:text-gray-500"
                    onClick={() => handleClick(item)}
                    disabled={!permission}
                  >
                    <CloseIcon className="h-4 w-4" />
                  </button>
                </PermissionSection>
              )}
            </div>
          </div>

          {item.productsTypeCode === ProductTypeCodes.capital && (
            <OatFiCreditLimit />
          )}
        </div>
      </PermissionSection>
    </div>
  );
};

export default ProgressProduct;
